import { clsx, type ClassValue } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

// HeyRoot's design system defines its own typography scale as Tailwind v4
// `--text-*` theme tokens (see packages/ui/src/tokens/tailwind.css @theme).
// tailwind-merge's default config only knows the stock t-shirt font sizes
// (text-xs … text-9xl), so it misclassifies these custom `text-<name>`
// utilities as *text colors*. That silently drops a real color utility when
// both land on one element — e.g. `text-brand-primary-fg text-control-cta`
// collapses to only the (colorless) font-size utility, so a primary CTA
// rendered via `cn(buttonClassName({ variant: 'primary' }), …)` loses its
// `text-brand-primary-fg` label color and inherits the default `text-primary`,
// which fails WCAG AA contrast on the `bg-brand-primary` fill. Registering the
// tokens as font sizes lets twMerge keep the color and the size side by side.
// Keep this list in sync with the `--text-*` base tokens declared in tailwind.css.
const CUSTOM_FONT_SIZES = [
  'metric-hero',
  'display',
  'heading-1',
  'heading-2',
  'heading-3',
  'body',
  'body-sm',
  // Typography v4 PR 3 (#12011) reading role — explanations, help text, form content.
  'body-reading',
  // Typography v4 PR 2 emphasis/strong/kbd roles (plan §4.1).
  'body-emphasis',
  'body-sm-emphasis',
  'body-strong',
  'body-sm-strong',
  'kbd',
  'caption',
  'eyebrow',
  'mono',
  'control-cta',
  // Typography v4 PR 3 (#12011) control-label roles — the label inside an
  // interactive control (Button, SplitButton), kept at 600 by owner ruling
  // 2026-09-19 while body-strong reads at 560.
  //
  // MISSING THIS ENTRY IS NOT COSMETIC. tailwind-merge defaults an unregistered
  // `text-*` class to the COLOUR group, so `text-control-label` collided with the
  // real colour utility and, being later in `buttonClassName`'s join order, WON:
  //   cn('bg-brand-primary text-brand-primary-fg text-control-label')
  //     -> 'bg-brand-primary text-control-label'   // fg colour silently deleted
  // That is exactly the tailwind-merge misclassification this file's own header
  // describes -- the one that shipped an ink-on-green CTA and an axe failure in
  // PR #5034. Four independent judges caught the omission on 2026-09-21; one
  // reproduced the deletion by executing the real `cn`.
  'control-label',
  'control-label-sm',
  'metric-sm',
  'metric',
  'metric-lg',
  'label',
  'table-header',
];

const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      'font-size': [{ text: CUSTOM_FONT_SIZES }],
    },
  },
});

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
